import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, Field, dialogCls, inputCls } from "@/components/admin/ui";

const BLANK = { name: "", service: "", body: "" };

export const TemplatesPanel = ({ templates, setTemplates }) => {
  const [editing, setEditing] = useState(null);

  const save = async () => {
    try {
      const body = { name: editing.name, service: editing.service, body: editing.body };
      const { data } = editing.id ? await api.put(`/admin/templates/${editing.id}`, body) : await api.post("/admin/templates", body);
      setTemplates((xs) => (xs.some((x) => x.id === data.id) ? xs.map((x) => (x.id === data.id ? data : x)) : [...xs, data]));
      setEditing(null);
      toast.success("Template saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template? Existing contracts are not affected.")) return;
    await api.delete(`/admin/templates/${id}`);
    setTemplates((xs) => xs.filter((x) => x.id !== id));
    setEditing(null);
  };

  return (
    <div data-testid="templates-panel">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-zinc-400">Placeholders: {"{{client_name}}"}, {"{{organization}}"}, {"{{date}}"} are filled in when you create a contract. Start a line with ## for a heading.</p>
        <Btn variant="primary" onClick={() => setEditing(BLANK)} data-testid="template-new-btn"><Plus className="h-4 w-4" /> New template</Btn>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {templates.map((t) => (
          <button key={t.id} onClick={() => setEditing(t)} className="rounded-2xl border border-[#2E2A24] bg-[#121215] p-5 text-left transition-[border-color] hover:border-[#C4832B]" data-testid={`template-card-${t.id}`}>
            <p className="font-display text-2xl text-[#F5EFE6]">{t.name}</p>
            <p className="mt-2 line-clamp-3 text-xs text-zinc-400">{t.body.slice(0, 220)}</p>
          </button>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing?.id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Field label="Template name"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="template-name-input" /></Field>
              <Field label="Contract text"><textarea rows={18} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={`${inputCls} font-mono-rc text-xs leading-relaxed`} data-testid="template-body-input" /></Field>
              <div className="flex justify-between">
                {editing.id ? <Btn variant="danger" onClick={() => remove(editing.id)} data-testid="template-delete-btn"><Trash2 className="h-4 w-4" /> Delete</Btn> : <span />}
                <Btn variant="primary" onClick={save} disabled={!editing.name || !editing.body} data-testid="template-save-btn">Save template</Btn>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
